import type { Category, IncomeStream, Wallet } from "./db";
import type { ParsedMoney, WalletHint } from "./quick-add";

// Turns a parsed quick-add money row into real rows: which wallet, which
// category or income stream. Pure, so it is unit tested and runs offline.

// Everyday words for the default categories (Q5). Matching by the category's
// own name or a #tag always works too, so renamed or new categories still match.
const CATEGORY_WORDS: Record<string, string[]> = {
	food: [
		"lunch",
		"breakfast",
		"dinner",
		"supper",
		"groceries",
		"rolex",
		"chapati",
		"matooke",
		"snack",
		"market",
	],
	transport: ["boda", "taxi", "fuel", "petrol", "uber", "bolt", "bus", "fare"],
	"airtime/data": ["airtime", "data", "bundle", "bundles", "internet", "wifi"],
	rent: ["rent", "landlord"],
	utilities: ["yaka", "umeme", "water", "electricity", "power", "gas"],
	family: ["family", "mum", "mom", "dad", "home", "school", "fees"],
	health: ["pharmacy", "clinic", "hospital", "medicine", "drugs", "doctor"],
	fun: ["movie", "drinks", "beer", "outing", "party", "game"],
	savings: ["savings", "save", "sacco"],
};

const STREAM_WORDS: Record<string, string[]> = {
	salary: ["salary", "pay", "payslip", "wage", "wages"],
	freelance: ["freelance", "gig", "client", "invoice", "project", "contract"],
};

const words = (text: string) =>
	text
		.toLowerCase()
		.split(/[^\p{L}\p{N}/]+/u)
		.filter(Boolean);

const usable = <T extends { archived: boolean; deleted_at: string | null }>(rows: T[]) =>
	rows.filter((r) => !r.archived && !r.deleted_at);

function pickWallet(hint: WalletHint | null, wallets: Wallet[]): Wallet | null {
	const live = usable(wallets);
	const byName = (re: RegExp) => live.find((w) => re.test(w.name));
	switch (hint) {
		case "cash":
			return live.find((w) => w.type === "cash") ?? null;
		case "bank":
			return live.find((w) => w.type === "bank") ?? null;
		case "airtel":
			return byName(/airtel/i) ?? null;
		case "momo":
			return (
				byName(/mtn|momo/i) ??
				live.find((w) => w.type === "momo" && !/airtel/i.test(w.name)) ??
				null
			);
		default:
			// No wallet typed: cash is the everyday default.
			return live.find((w) => w.type === "cash") ?? live[0] ?? null;
	}
}

function pickByName<T extends { name: string }>(
	rows: T[],
	tokens: string[],
	vocabulary: Record<string, string[]>,
): T | null {
	const name = (r: T) => r.name.toLowerCase();
	return (
		rows.find((r) => tokens.includes(name(r))) ??
		rows.find((r) =>
			tokens.some((t) =>
				name(r)
					.split(/[/\s]+/)
					.includes(t),
			),
		) ??
		rows.find((r) => tokens.some((t) => vocabulary[name(r)]?.includes(t))) ??
		null
	);
}

export type MoneyTargets = {
	wallet: Wallet | null;
	category: Category | null;
	stream: IncomeStream | null;
};

export function resolveMoney(
	parsed: ParsedMoney,
	data: { wallets: Wallet[]; categories: Category[]; streams: IncomeStream[] },
): MoneyTargets {
	const tokens = [...parsed.tags.map((t) => t.toLowerCase()), ...words(parsed.note)];
	const categories = usable(data.categories).filter((c) => c.kind === parsed.kind);
	return {
		wallet: pickWallet(parsed.wallet, data.wallets),
		category: pickByName(categories, tokens, CATEGORY_WORDS),
		stream:
			parsed.kind === "income" ? pickByName(usable(data.streams), tokens, STREAM_WORDS) : null,
	};
}
